import { useState, useEffect, Fragment } from 'react'
import {
  Button,
  Fa,
  SelectOptionType,
  TextHeader,
  showErrorMessage,
  showInfoMessage,
  showSuccessMessage,
  showWarningMessage,
} from '@eltoro-ui/components'
import {
  Campaignservicev1Campaign,
  Campaignservicev1Audience,
  Campaignservicev1OrderLine,
  Targetjobservicev1Audience,
  V1AudienceType,
  V1ProductType,
} from 'next-gen-sdk'
import { useLocation } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import {
  GoBackButton,
  PolygonMap,
  RetargetingPixel,
  WarningModal,
  WarningMsg,
} from 'Components'
import { AudienceLibraryPage } from 'Pages'
import { AudienceUploadStepper } from 'Components/AudienceUploadContainer/components'
import { useAppContext, useCampaignContext } from 'Contexts'
import { getApiConfig, getJobQuotes } from 'Requests'
import { EditingCancelUpdateButtons } from '../EditingCancelUpdateButtons'
import { AudienceEditOLTable } from './AudienceEditOLTable'
import { AssetEditHeader } from '../AssetEditHeader'
import { IMPRESSION_MIN } from '../shared'
import { dayjs } from 'Tools/dateUtils'
import {
  DetermineIfAllowedToAttachAudience,
  getCPMForAudience,
  getProductTypes,
  getTypeMoneyCost,
  poll,
  userFriendlyAudienceName,
} from 'Helpers'

export type ModifiedOrderLineType = Campaignservicev1OrderLine & {
  tempId?: string
}

export type QuoteStatusType = {
  olId?: string
  quotes?: {
    count: number
    quoteStatus?: {
      job: Targetjobservicev1Audience
      quoted?: boolean
      count?: number
    }[]
  }
}
type ExcludeIncludeType = {
  auds?: { id?: string; exclude?: boolean }[]
  olId?: string
}[]

export const AudienceEdit = ({
  className = '',
  selectedOls,
  campaign,
  onCloseModal,
  audienceMinimum = 1, // defaulting to 1 until we get requirements
  filterRules,
  setRefreshCampaign,
}: {
  className?: string
  selectedOls: Campaignservicev1OrderLine[]
  campaign: Campaignservicev1Campaign
  onCloseModal: () => void
  audienceMinimum?: number
  filterRules?: V1ProductType
  setRefreshCampaign?: (x: boolean) => void
}) => {
  const [view, setView] = useState<'library' | 'create'>()
  // prettier-ignore
  const [
    selectedAudienceType,
    setSelectedAudienceType,
  ] = useState<SelectOptionType | undefined>({
    value: 'address',
    label: 'IP Targeting',
  })
  const [modifiedOrderLines, setModifiedOrderLines] = useState<
    ModifiedOrderLineType[]
  >(selectedOls)
  // prettier-ignore
  const [includeExcludeAudiences, setIncludeExcludeAudiences] = useState<
    ExcludeIncludeType
  >([])
  const [submitting, setSubmitting] = useState(false)
  const [olQuotes, setOLQuotes] = useState<QuoteStatusType[]>()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalText, setConfirmModalText] = useState('')
  const [confirmedWarning, setConfirmedWarning] = useState(false)
  // prettier-ignore
  const [
    pendingAudience,
    setPendingAudience,
  ] = useState<Targetjobservicev1Audience>()
  const {
    campaignServiceApi,
    currentOrg,
    roles,
    isUser,
    isAdmin,
  } = useAppContext()
  const { setRefresh } = useCampaignContext()
  const { pathname } = useLocation()
  const isInCampaignCreation =
    pathname.includes('/campaigns/create') ||
    pathname.includes('/campaigns/edit')

  // Boolean indicating if any selected order lines are locked (added per request)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const anySelectedLocked = selectedOls.some((ol) => ol.locked)

  useEffect(() => {
    const handleGetQuotes = async () => {
      if (currentOrg?.id) {
        Promise.all(
          modifiedOrderLines.map(async (modOL) => {
            if (!modOL.audiences || !currentOrg?.id)
              return { olId: modOL.id || modOL.tempId }
            const quotes = await getJobQuotes(modOL.audiences, currentOrg.id)
            return {
              olId: modOL.id || modOL.tempId,
              quotes,
            } as QuoteStatusType
          }),
        ).then((result) => setOLQuotes(result))
      }
    }
    // prevent fetching when using select buttons in library, only do this when screen
    if (view !== 'library' && view !== 'create') handleGetQuotes()
  }, [currentOrg?.id, modifiedOrderLines, view])

  // Updates the campaign order lines on submit
  // Note- this component is also on the dashboard, so it shouldn't use methods from CampaignContext,
  // unless we plan to load the campaign when opening an order line on the dashboard?
  const handleUpdate = () => {
    if (!campaignServiceApi) return
    setSubmitting(true)

    // Attach audiences to order lines
    // For each modified orderline
    Promise.all(
      modifiedOrderLines.map(async (modOL) => {
        const originalOL = selectedOls.find((ol) => ol.id === modOL.id)
        // Update existing order line
        if (originalOL && modOL.id) {
          const audiencesToAttach = modOL.audiences?.reduce(
            (acc: { id: string; exclude: boolean }[], audience) => {
              // is audience in the original OL
              if (
                !originalOL.audiences?.find(
                  (originalAud) => originalAud.id === audience.id,
                ) &&
                audience.id
              )
                return [
                  ...acc,
                  { id: audience.id, exclude: audience.exclude || false },
                ]
              return acc
            },
            [],
          )
          const audiencesToDetach = originalOL.audiences?.reduce(
            (acc: string[], audience) => {
              // is audience in the original OL but NOT on the modOL
              if (
                !modOL.audiences?.find((modAud) => modAud.id === audience.id) &&
                originalOL.audiences?.find(
                  (originalAud) => originalAud.id === audience.id,
                ) &&
                audience.id
              )
                return [...acc, audience.id]
              return acc
            },
            [],
          )

          const includeExcludeUpdates = includeExcludeAudiences
            .find(({ olId }) => olId === modOL.id)
            ?.auds?.filter((aud) => {
              // should only include the updates for audiences that were already on the order line- new ones will include exclusions above
              const isOnOriginalOl = !!originalOL.audiences?.find(
                (originalAud) => originalAud.id === aud.id,
              )
              return isOnOriginalOl
            })

          await Promise.all([
            ...((audiencesToAttach || []).length > 0
              ? [
                  campaignServiceApi.advertisingPlatformServiceBatchAddAudiences(
                    modOL.id,
                    {
                      orgId: modOL.orgId,
                      audiences: audiencesToAttach,
                    },
                  ),
                ]
              : []),
            ...((audiencesToDetach || []).length > 0
              ? [
                  campaignServiceApi.advertisingPlatformServiceBatchRemoveAudiences(
                    modOL.id,
                    {
                      orgId: modOL.orgId,
                      audienceIds: audiencesToDetach,
                    },
                  ),
                ]
              : []),
            ...((includeExcludeUpdates || []).length > 0
              ? [
                  // batch remove so the exclusion can be updated
                  campaignServiceApi
                    .advertisingPlatformServiceBatchRemoveAudiences(modOL.id, {
                      orgId: modOL.orgId,
                      audienceIds: (includeExcludeUpdates || []).reduce(
                        (acc: string[], aud) => {
                          if (!aud.id) return acc
                          return [...acc, aud.id]
                        },
                        [],
                      ),
                    })
                    .then(async (res) => {
                      if (modOL.id) {
                        // batch add with new exclusion setting
                        const updatedOl = await campaignServiceApi.advertisingPlatformServiceBatchAddAudiences(
                          modOL.id,
                          {
                            orgId: modOL.orgId,
                            audiences: includeExcludeUpdates,
                          },
                        )
                        return updatedOl
                      }
                      return res
                    })
                    .catch((e) =>
                      showErrorMessage(
                        'Error updating audience exclusions',
                        e?.message || e?.body?.message || '',
                      ),
                    ),
                ]
              : []),
          ])
        }
        // Create new order line
        if (modOL.tempId) {
          // create
          if (!campaignServiceApi || !currentOrg?.id) return
          const now = dayjs().tz().add(1, 'day').startOf('day').utc().toDate()
          const nowPlusSeven = dayjs()
            .tz()
            .add(8, 'days')
            .endOf('day')
            .utc()
            .toDate()
          const orderLineCreationParameters = {
            name: modOL.name,
            impressions: currentOrg?.minimumImpressions || IMPRESSION_MIN,
            startTime: now,
            endTime: nowPlusSeven,
          }
          const createdOL = await campaignServiceApi.advertisingPlatformServiceCreateOrderLine(
            currentOrg.id,
            orderLineCreationParameters,
            campaign.id,
          )
          if (!createdOL?.id) return

          // waiting for draft status before attaching audiences (otherwise OL gets stuck in 'creating' status)
          const newOrderLine = await poll(
            (token) =>
              campaignServiceApi.advertisingPlatformServiceGetOrderLine(
                createdOL.id || '',
                currentOrg.id ? currentOrg.id : '',
                token ? getApiConfig(token) : undefined,
              ),
            (ol?: Campaignservicev1OrderLine) =>
              ol?.status !== 'ORDERLINE_STATUS_DRAFT',
            1000,
            900000,
          )
          if (!newOrderLine?.id) return
          const audiencesToAttachToNewOL = modOL.audiences?.reduce(
            (acc: { id: string; exclude: boolean }[], audience) => {
              if (audience.id)
                return [
                  ...acc,
                  { id: audience.id, exclude: audience.exclude || false },
                ]
              return acc
            },
            [],
          )
          await campaignServiceApi.advertisingPlatformServiceBatchAddAudiences(
            newOrderLine.id,
            {
              orgId: newOrderLine.orgId,
              audiences: audiencesToAttachToNewOL,
            },
          )
        }
      }),
    )
      .then(() => {
        showSuccessMessage('Success', 'Your order line audiences were updated')
        if (setRefreshCampaign) setRefreshCampaign(true)
        if (isInCampaignCreation) setRefresh(true)
        setSubmitting(false)
        onCloseModal()
      })
      .catch((e) =>
        showErrorMessage(
          'There was an error saving your order line audience changes',
          e?.message || e?.body?.message || '',
        ),
      )
  }

  const handleAddChange = (modifiedOL: ModifiedOrderLineType) => {
    setModifiedOrderLines((prevModifiedOLs) => {
      if (modifiedOL.tempId) {
        // if it has a temp id - (is new)
        if (!modifiedOL.audiences?.length)
          // if it does not have audiences, remove it
          return prevModifiedOLs.filter((ol) => ol.tempId !== modifiedOL.tempId)

        const prevIndex = prevModifiedOLs.findIndex(
          (p) => p.tempId === modifiedOL.tempId,
        )
        // if it existed, update in place
        if (prevIndex >= 0) {
          return [
            ...prevModifiedOLs.slice(0, prevIndex),
            modifiedOL,
            ...prevModifiedOLs.slice(prevIndex + 1),
          ]
        }
        // otherwise, add it to the end
        return [...prevModifiedOLs, modifiedOL]
      }

      // update non-temp ol in place
      const prevIndex = prevModifiedOLs.findIndex((p) => p.id === modifiedOL.id)
      return [
        ...prevModifiedOLs.slice(0, prevIndex),
        modifiedOL,
        ...prevModifiedOLs.slice(prevIndex + 1),
      ]
    })
  }

  // Creates new temp order line based on campaign name
  const addNewOrderLine = (newAudiences: Targetjobservicev1Audience[]) => {
    // add order line with tempId
    const tempOrderLineNames = [
      ...new Set(
        modifiedOrderLines.reduce((acc: string[], ol) => {
          if (ol.tempId && ol.name) return [...acc, ol.name]
          return acc
        }, []),
      ),
    ]
    let orderLineCount = campaign.orderLines?.length || 0
    if (tempOrderLineNames.length > 0) {
      orderLineCount = Number(
        tempOrderLineNames.slice(-1)[0].split('_').slice(-1),
      )
    }

    const audienceArray = newAudiences.reduce(
      (acc: Targetjobservicev1Audience[], aud) => {
        if (
          aud.type === 'AUDIENCE_TYPE_VR' &&
          aud.audiences &&
          aud.audiences.length > 0
        ) {
          return [...acc, ...aud.audiences]
        }
        return [...acc, aud]
      },
      [],
    )
    // add new temp ol
    const newOrderLine = {
      name: `${
        campaign?.name ? `${campaign.name}_` : ''
      }${newAudiences[0].type?.split('_').slice(-1)}_Draft_${
        orderLineCount + 1
      }`,
      audiences: audienceArray,
      tempId: uuid(),
    }
    handleAddChange(newOrderLine)

    return newOrderLine
  }

  // Adds audience to modified order lines in state
  // If order line info given, will move audience from source to target
  // or just apply to the source ol
  const handleAddAudience = (
    newAudiences: Targetjobservicev1Audience[],
    moddedOLs?: {
      sourceOL?: ModifiedOrderLineType // remove from this order line
      targetOL?: ModifiedOrderLineType // target order line
    },
    confirmation?: boolean,
  ) => {
    // FUTURE BEWARE: if we need to support different kinds of audiences in this newAudience array, that will likely introduce issues
    // for now we are only supporting same-kind audiences here, since all the uploaders are going to submit same-type audiences
    if (newAudiences.every((aud) => aud.type !== newAudiences[0].type)) {
      showErrorMessage('Error', 'Please select audiences of the same type.')
      return
    }

    const audienceArray = newAudiences.reduce(
      (acc: Targetjobservicev1Audience[], aud) => {
        if (
          aud.type === 'AUDIENCE_TYPE_VR' &&
          aud.audiences &&
          aud.audiences.length > 0
        ) {
          return [...acc, ...aud.audiences]
        }
        return [...acc, aud]
      },
      [],
    )

    // add to all order lines
    if (!moddedOLs) {
      const emptyOrderLines = modifiedOrderLines.filter(
        (ol) => !ol.audiences || ol.audiences.length === 0,
      )
      // Determine if an OL is not a draft,
      // if draft allow attach of any audience, if not create a new one.
      // any of the modified order lines, then make a new one
      if (
        emptyOrderLines &&
        !emptyOrderLines?.length &&
        currentOrg &&
        roles &&
        modifiedOrderLines.every((ol) => {
          return !DetermineIfAllowedToAttachAudience(
            ol.audiences || [],
            newAudiences[0],
            roles?.includes('nextgen_admin'),
          )
        })
      ) {
        const CreatedNewOlMessage = ({ prodType }: { prodType: string }) => {
          return (
            <>
              Attaching product type "{userFriendlyAudienceName(prodType)}" to
              is not allowed on your selection. We have created a new orderline
              for this audience.
              <br />
              <small>
                For assistance, please contact&nbsp;
                <a href="mailto:support@eltoro.com">support@eltoro.com</a> or
                your Sales Representative.
              </small>
            </>
          )
        }
        showWarningMessage(
          'Order line will be created',
          <CreatedNewOlMessage prodType={newAudiences[0].productType || ''} />,
        )
        return addNewOrderLine(newAudiences)
      }

      // attach audience to all compatible order lines
      // or add to order lines with empty audience array
      const arrayOfOlsNotAttached: string[] = []
      const arrayOfOLsAttached: string[] = []
      modifiedOrderLines.forEach((ol) => {
        const filteredAudiences = ol.audiences?.filter((existingOLAud) => {
          return !!audienceArray.find((aud) => aud.id !== existingOLAud.id)
        })
        if (!currentOrg) return

        const newAudiences = [...audienceArray]
          .reduce((acc: Campaignservicev1Audience[], currentAud) => {
            if (!filteredAudiences || filteredAudiences.length === 0) {
              return [...acc, currentAud]
            }
            if (!!acc.find((aud) => currentAud.id === aud.id)) {
              return acc
            }
            if (
              DetermineIfAllowedToAttachAudience(
                ol.audiences || [],
                currentAud,
                roles?.includes('nextgen_admin'),
              )
            ) {
              if (
                ol.highestCpmAudience &&
                ol.highestCpmAudience.audienceBasePrice &&
                ol.highestCpmAudience.audienceUpcharge &&
                ol.paid
              ) {
                if (currentAud.dataProduct === 'DATA_PRODUCT_CUSTOM_AUDIENCE') {
                  if (isUser) {
                    showWarningMessage(
                      'Unable to Attach Premium Audience',
                      'Please contact support if you want to attach this premium audience',
                    )
                    return acc
                  }
                  if (isAdmin) {
                    const foundDataSource = ol.dataSourcesHighestUpcharge?.find(
                      (ds) => {
                        return ds.dataSource === currentAud.dataSource
                      },
                    )
                    // New Datasource not on OL:
                    if (!foundDataSource) {
                      if (!confirmation && !confirmedWarning) {
                        setShowConfirmModal(true)
                        setPendingAudience(currentAud)
                        setConfirmModalText(
                          'Attaching new custom audiences will increase the budget. Are you sure?',
                        )
                        return acc
                      } else {
                        showInfoMessage(
                          '',
                          'The audience has been selected for attachment',
                        )
                        return [...acc, currentAud]
                      }
                    }

                    if (
                      foundDataSource &&
                      foundDataSource.upcharge &&
                      currentAud.cpmPlus
                    ) {
                      const olUpchargePrice = getTypeMoneyCost(
                        foundDataSource.upcharge,
                      )
                      const currentAudUpchargePrice = getTypeMoneyCost(
                        currentAud.cpmPlus,
                      )
                      if (
                        olUpchargePrice < currentAudUpchargePrice &&
                        !confirmation &&
                        !confirmedWarning
                      ) {
                        setShowConfirmModal(true)
                        setPendingAudience(currentAud)
                        setConfirmModalText(
                          'Attaching new custom audiences will increase the budget. Are you sure?',
                        )
                        return acc
                      } else {
                        showInfoMessage(
                          '',
                          'The audience has been selected for attachment',
                        )
                        return [...acc, currentAud]
                      }
                    }
                  }
                }
                const olPrice = getTypeMoneyCost(
                  ol.highestCpmAudience.audienceBasePrice,
                  [ol.highestCpmAudience.audienceUpcharge],
                )
                const audTypeMoneyCpm = getCPMForAudience(
                  currentAud,
                  currentOrg,
                  ol,
                )
                const audPrice = getTypeMoneyCost(audTypeMoneyCpm?.base || {}, [
                  audTypeMoneyCpm?.upcharge || {},
                ])
                if (audPrice <= olPrice) {
                  if (ol.name) {
                    arrayOfOLsAttached.push(ol.name)
                  }
                  return [...acc, currentAud]
                }
                if (audPrice > olPrice) {
                  if (ol.name) {
                    arrayOfOlsNotAttached.push(ol.name)
                  }
                  return acc
                }
              }
              // Non-prepay and deployed
              if (
                currentAud.dataProduct === 'DATA_PRODUCT_CUSTOM_AUDIENCE' &&
                ol.status !== 'ORDERLINE_STATUS_DRAFT'
              ) {
                if (isUser) {
                  if (!confirmation && !confirmedWarning) {
                    setShowConfirmModal(true)
                    setPendingAudience(currentAud)
                    setConfirmModalText(
                      'Attaching new custom audiences may increase the budget. Are you sure?',
                    )
                    return acc
                  } else {
                    showInfoMessage(
                      '',
                      'The audience has been selected for attachment',
                    )
                    return [...acc, currentAud]
                  }
                }
                if (isAdmin) {
                  const foundDataSource = ol.dataSourcesHighestUpcharge?.find(
                    (ds) => {
                      return ds.dataSource === currentAud.dataSource
                    },
                  )
                  // New Datasource not on OL:
                  if (!foundDataSource) {
                    if (!confirmation && !confirmedWarning) {
                      setShowConfirmModal(true)
                      setPendingAudience(currentAud)
                      setConfirmModalText(
                        'Attaching new custom audiences will increase the budget. Are you sure?',
                      )
                      return acc
                    } else {
                      showInfoMessage(
                        '',
                        'The audience has been selected for attachment',
                      )
                      return [...acc, currentAud]
                    }
                  }

                  if (
                    foundDataSource &&
                    foundDataSource.upcharge &&
                    currentAud.cpmPlus
                  ) {
                    const olUpchargePrice = getTypeMoneyCost(
                      foundDataSource.upcharge,
                    )
                    const currentAudUpchargePrice = getTypeMoneyCost(
                      currentAud.cpmPlus,
                    )
                    if (
                      olUpchargePrice < currentAudUpchargePrice &&
                      !confirmation &&
                      !confirmedWarning
                    ) {
                      setShowConfirmModal(true)
                      setPendingAudience(currentAud)
                      setConfirmModalText(
                        'Attaching new custom audiences will increase the budget. Are you sure?',
                      )
                      return acc
                    } else {
                      showInfoMessage(
                        '',
                        'The audience has been selected for attachment',
                      )
                      return [...acc, currentAud]
                    }
                  }
                }
              }

              if (ol.name) {
                arrayOfOLsAttached.push(ol.name)
              }
              return [...acc, currentAud]
            }
            if (ol.name) {
              arrayOfOlsNotAttached.push(ol.name)
            }
            return acc
          }, [])
          .concat(filteredAudiences || [])

        const updatedOl = {
          ...ol,
          audiences: newAudiences,
        }
        if (arrayOfOlsNotAttached && arrayOfOlsNotAttached.length > 0) {
          const FailedOrSuccessfullyAddedTo = () => {
            return (
              <>
                Attaching product type "
                {userFriendlyAudienceName(newAudiences[0].productType || '')}"
                is not allowed on the following order lines:
                <ul>
                  {arrayOfOlsNotAttached.map((c) => (
                    <li
                      className="list-item"
                      style={{ display: 'flex', paddingBottom: `${1}px` }}
                    >
                      <Fa type="solid" icon="fa-circle-dot" size={'xs'} />
                      <small>{c}</small>
                    </li>
                  ))}
                </ul>
                {arrayOfOLsAttached && arrayOfOLsAttached.length > 0 && (
                  <>
                    Audience will be attached to the following order lines:
                    <ul>
                      {arrayOfOLsAttached
                        ? arrayOfOLsAttached.map((c) => (
                            <li
                              className="list-item"
                              style={{ display: 'flex' }}
                            >
                              <Fa
                                icon="fa-circle-dot"
                                size={'xs'}
                                type="solid"
                              />
                              <small>{c}</small>
                            </li>
                          ))
                        : ''}
                    </ul>
                  </>
                )}
              </>
            )
          }

          showInfoMessage(
            'Audience unable to be attached to entire selection',
            <FailedOrSuccessfullyAddedTo />,
          )
        }
        handleAddChange(updatedOl)
      })
    }

    // moving order lines
    if (moddedOLs) {
      const { targetOL, sourceOL } = moddedOLs
      if (targetOL) {
        // add to target ol
        handleAddChange({
          ...targetOL,
          audiences: [...(targetOL?.audiences || []), ...audienceArray],
        })
      } else {
        // add to a new order line
        addNewOrderLine(newAudiences)
      }
      // remove from source ol
      if (sourceOL)
        handleAddChange({
          ...sourceOL,
          audiences: sourceOL.audiences?.filter(
            (aud) => !audienceArray.find((newAud) => newAud.id === aud.id),
          ),
        })
    }
  }

  // Remove audience from the specified order line
  // if no audience given, removes ALL audiences from that order line
  const handleRemoveAudience = (
    orderLine: ModifiedOrderLineType,
    audience?: Targetjobservicev1Audience,
    confirmation?: boolean,
  ) => {
    if (!currentOrg) return
    // do not remove if last audience on non-draft OL
    if (
      orderLine.audiences &&
      orderLine.audiences.length <= 1 &&
      orderLine.status !== 'ORDERLINE_STATUS_DRAFT' &&
      !orderLine.tempId
    )
      return

    if (audience) {
      // do not remove last highest audience if multiple audience types are on the ol
      const audsIfDetached =
        orderLine.audiences?.filter((aud) => aud.id !== audience?.id) || []
      const productsIfDetached = getProductTypes(audsIfDetached)
      if (
        orderLine?.highestCpmAudience &&
        orderLine?.highestCpmAudience?.audienceProductType !==
          'PRODUCT_TYPE_UNKNOWN' &&
        !productsIfDetached.includes(
          orderLine?.highestCpmAudience?.audienceProductType,
        )
      ) {
        if (orderLine.status !== 'ORDERLINE_STATUS_DRAFT') {
          showWarningMessage(
            'Unable to detach',
            <>
              <p className="flex flex-col gap-3 text-sm">
                Unable to remove the last{' '}
                {userFriendlyAudienceName(audience.productType || '')} audience,
                Please attach another
                {userFriendlyAudienceName(audience.productType || '')} audience
                before removing this audience
              </p>
            </>,
          )
        }
        return
      }

      if (
        !DetermineIfAllowedToAttachAudience(
          audsIfDetached,
          audience,
          roles?.includes('nextgen_admin'),
        ) &&
        !confirmation
      ) {
        if (orderLine.status !== 'ORDERLINE_STATUS_DRAFT') {
          showWarningMessage(
            'Unable to detach',
            <>
              <p className="flex flex-col gap-3 text-sm">
                Removing this audience will remove an admin only audience
                combination, Please attach another
                {userFriendlyAudienceName(audience.productType || '')} before
                removing this audience
              </p>
            </>,
          )
        } else {
          showWarningMessage(
            'Unable to detach',
            <>
              <p className="flex flex-col gap-3 text-sm">
                Removing this audience will remove an admin only audience
                combination, Please attach another &nbsp;
                {userFriendlyAudienceName(audience.productType || '')} before
                removing this audience or if you are sure:
                <Button
                  onClick={() => {
                    handleRemoveAudience(orderLine, audience, true)
                  }}
                >
                  Confirm Detachment
                </Button>
              </p>
            </>,
          )
        }
        return
      }
      if (audience.type === 'AUDIENCE_TYPE_VR') {
        // remove sub audiences from this order line (or this VR audience)
        const newAudiences = orderLine.audiences?.filter((oldAud) => {
          return (
            !audience.audiences?.find((aud) => aud.id === oldAud.id) &&
            oldAud.id !== audience.id
          )
        })

        handleAddChange({
          ...orderLine,
          audiences: newAudiences,
        })
        return
      }
      // remove audience from this order line
      handleAddChange({
        ...orderLine,
        audiences: orderLine.audiences?.filter(
          (oldAud) => oldAud.id !== audience.id,
        ),
      })
    } else {
      // remove all audiences from order line
      const products = getProductTypes(orderLine.audiences || [])
      if (products && products.length > 1 && !confirmation) {
        showWarningMessage(
          'Remove All Failed',
          <>
            <p className="flex flex-col gap-3 text-sm">
              You are about to remove all audiences from this order line. Are
              you sure you want to do this? (this will remove any audience
              combinations that are not allowed)
              <Button
                onClick={() => {
                  handleRemoveAudience(orderLine, undefined, true)
                }}
              >
                Confirm Removal
              </Button>
            </p>
          </>,
        )
        return
      }
      handleAddChange({
        ...orderLine,
        audiences: [],
      })
    }
  }
  const handleExcludeAudience = ({
    olId,
    audiencesToIncludeOrExclude,
  }: {
    olId: string
    audiencesToIncludeOrExclude: { id: string; exclude: boolean }[]
  }) => {
    const filter = includeExcludeAudiences.filter((cur) => {
      return cur.olId !== olId
    })
    const x = [
      ...new Set([
        ...filter,
        ...[{ olId: olId, auds: audiencesToIncludeOrExclude }],
      ]),
    ].flat()
    setIncludeExcludeAudiences(x)
    // update exclude/include (for new audiences)
    const orderLine = modifiedOrderLines.find((ol) => ol.id === olId)
    const newAudiences = orderLine?.audiences?.map((aud) => {
      const change = audiencesToIncludeOrExclude.find((a) => aud.id === a.id)
      if (change) return { ...aud, exclude: change.exclude }
      return aud
    })
    if (newAudiences) handleAddChange({ ...orderLine, audiences: newAudiences })
  }

  const hasUpdates = modifiedOrderLines.some((modOL) => {
    if (modOL.tempId) return true
    const matching = selectedOls.find(
      (selectedOl) => selectedOl.id === modOL.id,
    )
    if (!matching) return true
    const originalAudiences = matching.audiences
    const modAudiences = modOL.audiences
    if (
      originalAudiences &&
      modAudiences &&
      originalAudiences.length !== modAudiences.length
    )
      return true

    return !originalAudiences?.every((originalAudience) => {
      return modAudiences?.find((modAud) => modAud.id === originalAudience.id)
    })
  })

  const selected = selectedAudienceType?.value

  const notEnoughAudiences = !isInCampaignCreation
    ? (olQuotes || [])
        .filter((ol) => (ol.quotes?.count || 0) < audienceMinimum)
        .map((quote) => {
          const ol = modifiedOrderLines.find(
            (ol) => ol.id === quote.olId || ol.tempId === quote.olId,
          )
          return { name: ol?.name, ...quote }
        })
    : []

  if (view === 'library') {
    const attachedAudiences = modifiedOrderLines
      .map((ol) => ol.audiences)
      .flat()
      ?.reduce((acc: Campaignservicev1Audience[], currentAud) => {
        if (
          !currentAud ||
          !!acc.find((audience) => audience.id === currentAud.id)
        )
          return acc
        return [...acc, currentAud]
      }, [])
    const filterMaker = () => {
      if (!filterRules) {
        return undefined
      }
      if (filterRules === 'PRODUCT_TYPE_IP_LIST_FOR_MAILING')
        return ['AUDIENCE_TYPE_ADDRESSES_FOR_IP'] as V1AudienceType[]
      if (filterRules === 'PRODUCT_TYPE_IP_TARGETING') {
        return [
          'AUDIENCE_TYPE_B2B',
          'AUDIENCE_TYPE_B2C',
          'AUDIENCE_TYPE_ZIP',
          'AUDIENCE_TYPE_ADDRESS',
          'AUDIENCE_TYPE_IP',
          'AUDIENCE_TYPE_DEVICE',
        ] as V1AudienceType[]
      }
      if (filterRules === 'PRODUCT_TYPE_VENUE_REPLAY') {
        return ['AUDIENCE_TYPE_VR'] as V1AudienceType[]
      }
      if (filterRules === 'PRODUCT_TYPE_MAP_POLYGON') {
        return ['AUDIENCE_TYPE_IPSFORGEOJSON'] as V1AudienceType[]
      }
      if (filterRules === 'PRODUCT_TYPE_NEW_MOVERS') {
        return [
          'AUDIENCE_TYPE_ESCROWMOVER',
          'AUDIENCE_TYPE_PREMOVER',
          'AUDIENCE_TYPE_POSTMOVER',
        ] as V1AudienceType[]
      }
      if (filterRules === 'PRODUCT_TYPE_DIGITAL_CANVASSING') {
        return ['AUDIENCE_TYPE_DC'] as V1AudienceType[]
      }
      if (filterRules === 'PRODUCT_TYPE_RETARGETING') {
        return ['AUDIENCE_TYPE_RETARGETING'] as V1AudienceType[]
      }
      if (filterRules === 'PRODUCT_TYPE_UNKNOWN') {
        return undefined
      }
      if (filterRules) return undefined
    }
    return (
      <>
        {showConfirmModal && (
          <WarningModal
            onConfirm={() => {
              setConfirmModalText('')
              if (pendingAudience) {
                handleAddAudience([pendingAudience], undefined, true)
              }
              setShowConfirmModal((prevValue) => !prevValue)
              setConfirmedWarning(true)
            }}
            onCancel={() => {
              setShowConfirmModal(false)
              setConfirmModalText('')
              setPendingAudience(undefined)
            }}
            confirm
          >
            <p className="text-md flex flex-col gap-3">{confirmModalText}</p>
          </WarningModal>
        )}
        <div className="AudienceEdit__wrap flex items-center justify-end gap-4 py-2 pr-5">
          <GoBackButton
            onClick={() => setView(undefined)}
            position="top-right"
            classWrap={`Library--goback ${selected}`}
            className={hasUpdates ? 'ripples' : ''}
          />

          <AudienceLibraryPage
            overrideModalPreview
            selectedAudiences={attachedAudiences}
            onAddAudienceToAttach={handleAddAudience}
            onRemoveAudienceFromAttach={(audienceToRemove) =>
              modifiedOrderLines.forEach((modAud) =>
                handleRemoveAudience(modAud, audienceToRemove),
              )
            }
            filterByType={undefined}
          />
        </div>
      </>
    )
  }

  if (view === 'create') {
    const selectedTitle = selectedAudienceType?.label
    return (
      <div
        className={`AudienceEdit__upload flex flex-col py-1 ${className} AudienceEdit__upload-${selected}`}
      >
        <div className="AudienceEdit__upload-header flex items-center justify-between py-2">
          <TextHeader className="flex items-center gap-2" type={5}>
            <span>Create</span>
            <span className="text-tint-gray-500 scale-y-150">&#62;</span>
            {selectedTitle}
          </TextHeader>
        </div>
        <GoBackButton
          onClick={() => setView(undefined)}
          position="top-right"
          classWrap={`AudienceEdit__upload--goback ${selected}`}
          className={hasUpdates ? 'ripples' : ''}
        />
        {(() => {
          if (selectedAudienceType?.value === 'polygon')
            return (
              <PolygonMap
                vr={false}
                onAddAudienceToAttach={handleAddAudience}
              />
            )
          if (selectedAudienceType?.value === 'venue-replay')
            return <PolygonMap vr onAddAudienceToAttach={handleAddAudience} />
          if (selectedAudienceType?.value === 'retargeting')
            return (
              <RetargetingPixel onAddAudienceToAttach={handleAddAudience} />
            )
          if (selectedAudienceType?.value)
            return (
              <AudienceUploadStepper
                audienceType={selectedAudienceType.value}
                onAddAudienceToAttach={handleAddAudience}
              />
            )
        })()}
      </div>
    )
  }

  return (
    <div className={`AudienceEdit flex flex-col ${className}`}>
      <AssetEditHeader
        selectedType={selectedAudienceType}
        setSelectedType={setSelectedAudienceType}
        setView={setView}
        modifiedOrderLines={modifiedOrderLines}
        handleAddAudience={handleAddAudience}
      />
      <AudienceEditOLTable
        modifiedOrderLines={modifiedOrderLines}
        handleAddAudience={handleAddAudience}
        handleRemoveAudience={handleRemoveAudience}
        handleExcludeOrIncludeAudiences={handleExcludeAudience}
      />
      {notEnoughAudiences.length > 0 && (
        <WarningMsg>
          The following order lines do not meet the minimum audience count of{' '}
          {audienceMinimum}. Please add sufficient audiences to these order
          lines:&nbsp;
          {notEnoughAudiences.map((olQuote, i) => (
            <Fragment key={`${olQuote.olId}_${i}`}>
              <span className="font-bold">{olQuote.name}</span>
              {`${i !== notEnoughAudiences.length - 1 ? ', ' : '.'}`}
            </Fragment>
          ))}
        </WarningMsg>
      )}
      <EditingCancelUpdateButtons
        onCloseModal={onCloseModal}
        handleUpdate={handleUpdate}
        updateDisabled={
          (!hasUpdates && includeExcludeAudiences.length === 0) ||
          (notEnoughAudiences.length > 0 &&
            includeExcludeAudiences.length === 0) ||
          submitting
        }
      />
    </div>
  )
}
